import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { ConsultationFeeAlert, type ConsultationInvoiceRef } from '../../ConsultationFeeAlert';
import { isAdmissionScope } from './draft';
import { OrderComposer } from './OrderComposer';
import { OrderLinesList } from './OrderLinesList';
import type { OrderKind, OrderPatient, OrderScope } from './types';

export interface OrderSectionProps {
  scope: OrderScope;
  patient: OrderPatient;
  readOnly: boolean;
  fee?: { id: string; context: string; status: string; consultationInvoice?: ConsultationInvoiceRef | null };
  onChanged: () => void;
  onAttach?: () => void;
  onPlaced?: () => void;
  initialSearch?: string;
  children?: ReactNode;
}

export function OrderSection({ kind, scope, patient, readOnly, fee, onChanged, onAttach, onPlaced, initialSearch, children }: OrderSectionProps & { kind: OrderKind }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['order-lines', scope.encounterId ?? scope.admissionId] });
    void queryClient.invalidateQueries({ queryKey: ['authorisation'] });
    onChanged();
  };
  const placed = () => {
    if (isAdmissionScope(scope)) {
      void queryClient.invalidateQueries({ queryKey: ['ipd-orders', scope.admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-tasks', scope.admissionId] });
    }
    changed();
    onPlaced?.();
  };
  return (
    <div className="space-y-4">
      {!readOnly && fee && <ConsultationFeeAlert encounter={fee} />}
      {!readOnly && can('order.write') && <OrderComposer kind={kind} scope={scope} patient={patient} initialSearch={initialSearch} onPlaced={placed} />}
      <OrderLinesList kind={kind} scope={scope} readOnly={readOnly} onChanged={changed} onAttach={onAttach} />
      {children}
    </div>
  );
}
